const DEFAULT_BUCKET_BASE_URL = "https://cdn.hytc.cloud/mai"

const splitEnvList = (value) => {
  return String(value || "")
    .split(/[\n,;]/g)
    .map((entry) => entry.trim())
    .filter(Boolean)
}

const getSameOriginBaseUrl = () => {
  if (typeof window === "undefined") {
    return ""
  }

  const origin = window.location?.origin
  if (!origin) {
    return ""
  }

  return `${origin.replace(/\/+$/g, "")}/mai`
}

const CLOUD_BASE_URL_CANDIDATES = Array.from(
  new Set(
    [
      ...splitEnvList(process.env.REACT_APP_MAI2_BUCKET_BASE_URLS),
      process.env.REACT_APP_MAI2_BUCKET_BASE_URL,
      process.env.REACT_APP_MAI2_CLOUD_BASE_URL,
      getSameOriginBaseUrl(),
      DEFAULT_BUCKET_BASE_URL
    ].filter(Boolean)
  )
)

const COLLECTION_CATEGORIES = [
  {
    key: "chara",
    label: "旅行伙伴",
    assetDir: "chara",
    thumbnailDir: "chara",
    xmlPath: "nowxml/chara/CharaSort.xml"
  },
  {
    key: "frame",
    label: "背景",
    assetDir: "frame",
    thumbnailDir: "frame_s",
    xmlPath: "nowxml/frame/FrameSort.xml"
  },
  {
    key: "icon",
    label: "头像",
    assetDir: "icon",
    thumbnailDir: "icon",
    xmlPath: "nowxml/icon/IconSort.xml"
  },
  {
    key: "partner",
    label: "搭档",
    assetDir: "partner",
    thumbnailDir: "partner",
    xmlPath: "nowxml/partner/PartnerSort.xml"
  },
  {
    key: "nameplate",
    label: "姓名框",
    assetDir: "nameplate",
    thumbnailDir: "nameplate",
    xmlPath: "nowxml/plate/PlateSort.xml"
  },
  {
    key: "jacket",
    label: "歌曲",
    assetDir: "jacket",
    thumbnailDir: "jacket",
    xmlPath: "nowxml/music/MusicSort.xml"
  }
]

const CATEGORY_MAP = COLLECTION_CATEGORIES.reduce((acc, item) => {
  acc[item.key] = item
  return acc
}, {})

const xmlNameCache = new Map()
const categoryDataCache = new Map()
let assetManifestCache = null
let activeBaseUrl = null

const getCategory = (categoryKey) => {
  return CATEGORY_MAP[categoryKey] || CATEGORY_MAP.chara
}

const trimSlash = (value) => {
  return String(value || "").replace(/^\/+|\/+$/g, "")
}

const buildCloudUrl = (path, baseUrl = "") => {
  const resolvedBase =
    baseUrl || activeBaseUrl || CLOUD_BASE_URL_CANDIDATES[0] || ""
  const base = String(resolvedBase || "").replace(/\/+$/g, "")
  const normalizedPath = trimSlash(path)

  if (!base || !normalizedPath) {
    return ""
  }

  return `${base}/${normalizedPath}`
}

const getCloudUrlCandidates = (path) => {
  const normalizedPath = trimSlash(path)

  if (!normalizedPath) {
    return []
  }

  const preferred = activeBaseUrl ? [activeBaseUrl] : CLOUD_BASE_URL_CANDIDATES
  const fallbacks = CLOUD_BASE_URL_CANDIDATES.filter(
    (baseUrl) => baseUrl !== activeBaseUrl
  )

  return [...preferred, ...fallbacks]
    .map((baseUrl) => ({
      baseUrl,
      url: buildCloudUrl(normalizedPath, baseUrl)
    }))
    .filter((entry) => entry.url)
}

const appendRequestMarker = (url) => {
  if (!url) {
    return ""
  }

  const separator = url.includes("?") ? "&" : "?"
  // Use a stable marker so app requests avoid mixed cache keys from manual opens.
  return `${url}${separator}app=mai2-web`
}

const buildAssetUrl = (dir, fileName) => {
  const normalizedDir = trimSlash(dir)

  if (!normalizedDir || !fileName) {
    return ""
  }

  return buildCloudUrl(
    `assets/assetbundle/${normalizedDir}/${encodeURIComponent(fileName)}`
  )
}

const fetchAssetManifest = async () => {
  if (assetManifestCache) {
    return assetManifestCache
  }

  const manifestPaths = ["manifest.json", "assets/assetbundle/manifest.json"]
  const attemptedUrls = []

  for (const manifestPath of manifestPaths) {
    const candidates = getCloudUrlCandidates(manifestPath)

    for (const { baseUrl, url } of candidates) {
      const requestUrl = appendRequestMarker(url)
      attemptedUrls.push(requestUrl)
      const response = await fetch(requestUrl)

      if (!response.ok) {
        continue
      }

      const manifest = await response.json()
      assetManifestCache = manifest
      activeBaseUrl = baseUrl

      return manifest
    }
  }

  throw new Error(
    `无法读取资源清单 manifest.json。已尝试: ${attemptedUrls.join(" , ")}`
  )
}

const normalizeManifestEntries = (manifest, dir) => {
  const rawEntries = manifest?.[dir]

  if (Array.isArray(rawEntries)) {
    return rawEntries
  }

  if (!rawEntries || typeof rawEntries !== "object") {
    return []
  }

  return Object.entries(rawEntries)
    .map(([key, value]) => {
      if (typeof value === "string") {
        return {
          id: Number(key),
          file: value
        }
      }

      if (value && typeof value === "object") {
        const id = Number(value.id ?? key)
        const file = value.file || value.filename || value.name

        return {
          id,
          file
        }
      }

      return null
    })
    .filter(Boolean)
}

const getImageMapByManifest = (manifest, dir) => {
  const imageMap = new Map()
  const entries = normalizeManifestEntries(manifest, dir)

  for (const { id, file } of entries) {
    if (typeof id !== "number" || !file || imageMap.has(id)) {
      continue
    }

    const url = buildAssetUrl(dir, file)
    if (!url) {
      continue
    }

    imageMap.set(id, url)
  }

  return imageMap
}

const fetchXmlNameMap = async (xmlPath) => {
  if (xmlNameCache.has(xmlPath)) {
    return xmlNameCache.get(xmlPath)
  }

  const xmlUrlCandidates = getCloudUrlCandidates(xmlPath)
  let lastStatus = ""

  for (const { baseUrl, url } of xmlUrlCandidates) {
    const response = await fetch(appendRequestMarker(url))

    if (!response.ok) {
      lastStatus = String(response.status)
      continue
    }

    const xmlText = await response.text()
    const xmlDoc = new DOMParser().parseFromString(xmlText, "text/xml")
    const map = new Map()
    const idNodes = Array.from(xmlDoc.querySelectorAll("StringID") || [])

    for (const node of idNodes) {
      const idText = node.querySelector("id")?.textContent?.trim()
      const name = node.querySelector("str")?.textContent?.trim()
      const id = Number(idText)

      if (Number.isNaN(id) || !name) {
        continue
      }

      map.set(id, name)
    }

    activeBaseUrl = baseUrl
    xmlNameCache.set(xmlPath, map)
    return map
  }

  throw new Error(
    `无法读取名称映射 XML（${lastStatus || "network"}）：${xmlPath}`
  )
}

const loadCategoryData = async (categoryKey) => {
  const category = getCategory(categoryKey)
  const cached = categoryDataCache.get(category.key)

  if (cached) {
    return cached
  }

  const loadPromise = (async () => {
    const manifest = await fetchAssetManifest()
    const fullImageMap = getImageMapByManifest(manifest, category.assetDir)
    const thumbnailMap = getImageMapByManifest(
      manifest,
      category.thumbnailDir || category.assetDir
    )

    const [nameMap] = await Promise.all([
      fetchXmlNameMap(category.xmlPath),
      Promise.resolve(true)
    ])

    const items = Array.from(fullImageMap.entries())
      .map(([id, fullImage]) => {
        const name = nameMap.get(id) || `未命名 ${id}`
        const thumbnailImage = thumbnailMap.get(id) || fullImage

        return {
          id,
          nickname: name,
          title: name,
          image: thumbnailImage,
          previewImage: fullImage
        }
      })
      .sort((a, b) => a.id - b.id)

    return items
  })()

  categoryDataCache.set(category.key, loadPromise)
  return loadPromise
}

const searchCollectionItems = (items, keyword) => {
  const normalizedKeyword = keyword.trim().toLowerCase()

  if (!normalizedKeyword) {
    return items
  }

  return items.filter((item) => {
    return (
      String(item.id).includes(normalizedKeyword) ||
      item.title.toLowerCase().includes(normalizedKeyword) ||
      item.nickname.toLowerCase().includes(normalizedKeyword)
    )
  })
}

export const getCollectionCategories = () => COLLECTION_CATEGORIES

export const resolveCategoryKey = (categoryKey) => getCategory(categoryKey).key

export const getCategoryLabel = (categoryKey) => getCategory(categoryKey).label

export const getCollectionsByCategory = async (categoryKey, keyword = "") => {
  const resolvedCategory = getCategory(categoryKey)
  const items = await loadCategoryData(resolvedCategory.key)

  return searchCollectionItems(items, keyword)
}
